/**
 * NỘI DUNG TRANG CÔNG CỤ TẠO VIDEO SO SÁNH HÀNG LOẠT (/tools/tao-video-so-sanh-hang-loat)
 *
 * Dùng chung trang VideoBatchToolPage — cấu trúc trường giống videoBatchToolContent.js.
 *
 * Công cụ + file hướng dẫn quá nặng nên để trên Google Drive, KHÔNG ở storage/tools. Link thư mục chỉ khai báo ở máy chủ
 * (EXTERNAL_TOOLS['compare-video-tool'], pharmacy-tool.controller.js); người đã mua bấm tải thì API trả link, trang mở ở tab mới
 * (externalDownload: true). Cần mua gói TOOL_VIDEO_SO_SANH (100.000đ / 12 tháng).
 * Lưu ý: ai có link Drive đều tải được — đổi link (tạo thư mục mới) nếu link bị chia sẻ ra ngoài.
 * Các bước cài đặt bên dưới viết chung (26/10/2026) vì chưa đối chiếu được file hướng dẫn trên Drive — có file thì sửa lại
 * `platforms`, `requirements`, `installSteps` cho khớp.
 */
export const COMPARE_VIDEO_TOOL = {
  published: true,

  slug: 'tao-video-so-sanh-hang-loat',
  contentFile: 'compareVideoToolContent.js',
  name: 'Công Cụ Tạo Video So Sánh Hàng Loạt',
  tagline: 'Làm hàng loạt video dọc so sánh hai đối tượng có người dẫn và lời thoại, đăng TikTok / Reels',
  intro:
    'Mỗi video đặt hai đối tượng cạnh nhau trên nền nhà thuốc, người dẫn chỉ vào từng bên kèm khung lời thoại giải thích điểm khác nhau. ' +
    'Chuẩn bị danh sách chủ đề so sánh, công cụ làm lần lượt ra video khung dọc 9:16.',
  version: '',
  fileSize: '',
  updatedAt: '26/10/2026',
  platforms: ['Máy tính'],
  image: '/images/tools/tool-video-so-sanh-banner.jpg',
  heroBanner: '/images/tools/tool-video-so-sanh-banner.jpg',

  externalDownload: true,
  downloadLabel: 'Tải công cụ & hướng dẫn',
  loginLabel: 'Đăng nhập để mua / tải công cụ',

  // YouTube Shorts (chỉ điền ID): youtube.com/shorts/vQNrNxyUWlc, youtube.com/shorts/2T1TNZujjaA
  sampleVideos: [
    { youtubeId: 'vQNrNxyUWlc', title: 'Video mẫu 1' },
    { youtubeId: '2T1TNZujjaA', title: 'Video mẫu 2' },
  ],
  sampleVideosNote: 'Hai video so sánh làm ra từ công cụ.',

  highlights: [
    { value: '2 bên', label: 'đặt cạnh nhau, người dẫn chỉ vào từng bên' },
    { value: 'Hàng loạt', label: 'làm nhiều chủ đề so sánh một lần' },
    { value: '100.000đ', label: 'dùng 12 tháng, tải về mỗi khi có cập nhật mới' },
  ],

  features: [
    { title: 'So sánh trực quan', desc: 'Hai đối tượng đặt cạnh nhau có tiêu đề lớn, người xem thấy ngay điểm khác biệt.' },
    { title: 'Có người dẫn và lời thoại', desc: 'Người dẫn chỉ vào từng bên, khung lời thoại tô đỏ từ khóa giúp người xem theo kịp nội dung.' },
    { title: 'Làm hàng loạt', desc: 'Chuẩn bị danh sách chủ đề, công cụ làm lần lượt từng video, không phải dựng tay.' },
    { title: 'Khung dọc 9:16', desc: 'Video ra đúng khổ TikTok, Reels, Shorts, đăng được ngay.' },
  ],

  audience: [
    'Chủ nhà thuốc làm video kiến thức dược, sức khỏe dạng so sánh',
    'Nhân viên marketing nhà thuốc cần ra nhiều video mỗi tuần',
  ],

  requirements: [
    { label: 'RAM', value: 'Tối thiểu 8 GB' },
    { label: 'Ổ cứng trống', value: 'Tối thiểu 1,5 GB để cài và chạy công cụ (chưa tính video tạo ra)' },
    { label: 'Tải công cụ', value: 'Tài khoản Google hoặc trình duyệt mở được Google Drive' },
    { label: 'Hướng dẫn', value: 'File hướng dẫn chi tiết nằm cùng thư mục Drive với công cụ' },
  ],

  installTitle: 'Hướng dẫn tải và dùng công cụ',
  installSubtitle: 'Hướng dẫn chi tiết có trong thư mục tải về. Dưới đây là các bước chính.',
  installTabLabel: 'Các bước',

  installSteps: [
    {
      title: 'Mua và mở thư mục tải',
      desc: 'Bấm "Tải công cụ & hướng dẫn". Sau khi mua, thư mục Google Drive chứa công cụ và file hướng dẫn mở ở tab mới.',
      tip: 'Trình duyệt chặn cửa sổ bật lên thì bấm cho phép rồi bấm tải lại.',
    },
    {
      title: 'Tải về máy',
      desc: 'Trong Google Drive, chọn công cụ và file hướng dẫn → bấm "Tải xuống". File nặng nên chờ tải xong hẳn rồi mới mở.',
    },
    {
      title: 'Làm theo file hướng dẫn',
      desc: 'Mở file hướng dẫn trong thư mục vừa tải và làm theo từng bước để cài đặt và tạo video so sánh đầu tiên.',
    },
  ],

  installStepsMac: [],

  youtubeVideoId: '',

  faqs: [
    {
      q: 'Công cụ giá bao nhiêu?',
      a: '100.000đ, dùng 12 tháng, tải lại nhiều lần trong thời gian đó. Thanh toán bằng QR chuyển khoản, mở tải ngay sau khi chuyển khoản thành công.',
    },
    {
      q: 'Vì sao tải từ Google Drive?',
      a: 'Bộ công cụ và hướng dẫn khá nặng nên được để trên Google Drive cho tải nhanh, ổn định. Website chỉ mở thư mục cho người đã mua.',
    },
    {
      q: 'Bấm tải mà không thấy gì mở ra?',
      a: 'Trình duyệt đang chặn cửa sổ bật lên. Bấm biểu tượng chặn ở thanh địa chỉ, chọn cho phép với website rồi bấm tải lại.',
    },
  ],

  support: {
    text: 'Cài đặt hoặc làm video gặp khó khăn? Gọi hoặc nhắn Zalo đội hỗ trợ kèm ảnh chụp màn hình.',
    phone: '0812539539',
    phoneLabel: '0812.539.539',
  },

  ctaTitle: 'Sẵn sàng làm video so sánh hàng loạt?',
  ctaText: 'Tải công cụ, làm theo hướng dẫn và ra video so sánh đầu tiên.',
};
